import ApplicationController from "./application_controller"
import GvToast from "../lib/gv_toast"

// Action helper for the organization opportunities-listing page
// (opportunities/givsum3/index_v3.html.haml and its partials). Shares one fetch/CSRF
// helper (request()) across a few unrelated per-card actions:
//   - open() (click->...#open): plain PUT actions with no form of their own, in the
//     "Pending Review" section (_pending_review_row_*_v3.html.haml) -- Waitlist, host
//     Confirm, Approve/Deny, Contribution/Donation Confirm. Immediate mode
//     (data-...-immediate-value="true") fires straight away; otherwise shows the shared
//     confirm dialog (_pending_review_confirm_modal_v3.html.haml) first. On success the row
//     is removed from the DOM and the "N Items Pending Review" count updates in place.
//   - submitForm() (submit->...#submitForm): for the Refute forms (real textarea input),
//     rendered inside a modal-ajax fragment -- intercepts the native submit so it gets the
//     same fetch/toast treatment instead of a full-page navigation. Also removes a row.
//   - toggleVisibility() (change->...#toggleVisibility): the "Event Visible" switch on
//     every card (_opp_listing_card_shell.html.haml). Not a review action -- nothing to
//     remove, it's a persistent per-card setting -- so it reuses request() directly instead
//     of going through submit(), and reverts the switch on failure.
// None of these reload the page.
export default class extends ApplicationController {
  // This app's "stimulus" package (2.0.0, pre-@hotwired rename) only supports the bare
  // shorthand form (name: String) for static values -- the { type, default } object form
  // throws "Unknown value type constant" during registration (parseValueTypeConstant gets
  // the whole object instead of a type constant). That throw aborts Application#load()'s
  // registration loop entirely, silently unregistering every controller alphabetically
  // after this file. Defaults are applied manually in code below instead.
  static values = {
    message: String,
    url: String,
    method: String,
    immediate: Boolean,
    successMessage: String
  }

  open(event) {
    event.preventDefault()
    const row = this.element.closest(".pending-review-row")

    if (this.immediateValue) {
      this.submit(this.urlValue, this.methodValue, null, { row })
      return
    }

    const modalEl = document.getElementById("pending-review-confirm-modal-v3")
    const messageEl = document.getElementById("pending-review-confirm-modal-v3-message")
    const submitBtn = document.getElementById("pending-review-confirm-modal-v3-submit")
    messageEl.textContent = this.messageValue

    const handleSubmit = () => {
      instance.hide()
      this.submit(this.urlValue, this.methodValue, null, { row })
      submitBtn.removeEventListener("click", handleSubmit)
    }
    submitBtn.addEventListener("click", handleSubmit)

    const instance = bootstrap.Modal.getOrCreateInstance(modalEl)
    instance.show()
  }

  // Refute forms (contributions/givsum3/confirm_refute_v3, donations/givsum3/confirm_refute_v3)
  // carry data-controller/data-action directly on the <form> tag, plus data-row-id since the
  // form is fetched into a modal appended to <body> -- disconnected from the row that opened it.
  submitForm(event) {
    event.preventDefault()
    const form = event.target
    const row = form.dataset.rowId ? document.getElementById(form.dataset.rowId) : null
    const modal = form.closest(".modal")
    this.submit(form.action, form.method, new FormData(form), { row, modal })
  }

  // "Event Visible" switch -- opportunities/campaigns both point their card's switch at
  // this same action; the toggled boolean is named "visible" server-side either way
  // (OpportunitiesController#update_visibility / CampaignsController#update_visibility each
  // flip their own model's field from it). Optimistic: the checkbox already reflects the
  // click before this fires, so a failed request has to flip it back.
  toggleVisibility(event) {
    const checkbox = event.target
    const visible = checkbox.checked
    checkbox.disabled = true

    this.request(this.urlValue, this.methodValue || "put", new URLSearchParams({ visible }))
      .then((succeeded) => {
        if (!succeeded) throw new Error("HTTP error")
        GvToast.success(visible ? "Event is now visible to everyone." : "Event is now private.")
      })
      .catch(() => {
        checkbox.checked = !visible
        GvToast.error("Something went wrong — please try again.")
      })
      .finally(() => {
        checkbox.disabled = false
      })
  }

  async submit(url, method, body, { row, modal } = {}) {
    try {
      const succeeded = await this.request(url, method, body)
      if (!succeeded) throw new Error("HTTP error")
      GvToast.success(this.successMessageValue || "Done.")
      if (modal) bootstrap.Modal.getInstance(modal)?.hide()
      this.removeRow(row)
    } catch (error) {
      GvToast.error("Something went wrong — please try again.")
    }
  }

  // redirect: "manual" -- these actions redirect_to on success (e.g. back to the org
  // dashboard). Letting fetch() follow that redirect can fail client-side (a redirect
  // crossing to a different host/custom domain isn't a CORS-safe follow) even though the
  // request itself already mutated the record server-side before the redirect was ever
  // sent. An opaque redirect response IS the success case here, not an error. Accept:
  // text/html pins format negotiation to the controller's format.html branch (the one
  // that redirects) so it can't accidentally land on format.json instead.
  async request(url, method, body) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    // A dropped/hung connection (dev-server reload, flaky wifi) would otherwise leave the
    // caller's checkbox/button disabled forever with no error -- cap it so it always
    // settles one way or the other.
    const timeout = AbortSignal.timeout ? AbortSignal.timeout(15000) : undefined

    const response = await fetch(url, {
      method: method || "put",
      redirect: "manual",
      body,
      signal: timeout,
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": token,
        "Accept": "text/html"
      }
    })
    return response.ok || response.type === "opaqueredirect"
  }

  removeRow(row) {
    if (!row) return
    row.remove()
    this.updateCount()
  }

  updateCount() {
    const list = document.getElementById("pending-review-list")
    const countText = document.getElementById("pending-review-count-v3-text")
    if (!list || !countText) return

    const remaining = list.querySelectorAll(".pending-review-row").length
    if (remaining === 0) {
      document.querySelector(".pending-review-card")?.remove()
      return
    }
    countText.textContent = `Pending Approvals (${remaining})`
  }
}
